import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PcoAppSettings } from "@/components/PcoAppSettings";
import { PcoConnect } from "@/components/PcoConnect";
import { AdminChurches } from "@/components/AdminChurches";
import { AdminReports } from "@/components/AdminReports";
import { StripeReconcile } from "@/components/StripeReconcile";

const empty = { code: "", months: "", plan: "", max_redemptions: "", note: "" };

export default function AdminCodes() {
  const { isAdmin, church } = useAuth();
  const [codes, setCodes] = useState([]);
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/codes").then((r) => setCodes(r.data)), []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/codes", {
        code: f.code || null, note: f.note,
        months: f.months ? Number(f.months) : null,
        plan: f.plan || null,
        max_redemptions: f.max_redemptions ? Number(f.max_redemptions) : null,
      });
      toast.success(`Code ${data.code} created`);
      setF(empty);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not create code");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (c) => { await api.patch(`/admin/codes/${c.code_id}`); load(); };
  const copy = (code) => navigator.clipboard.writeText(code).then(() => toast.success("Code copied"));

  return (
    <div className="fade-up">
      <p className="eyebrow">Platform owner</p>
      <h1 className="font-display text-navy text-4xl font-semibold tracking-tight mt-2">Discount codes</h1>
      <p className="text-muted-ink mt-2 max-w-2xl">Create codes your client churches can enter on their Billing page to get Dream Teams free, without paying for a subscription.</p>

      <form onSubmit={create} className="card-soft p-8 mt-8 grid md:grid-cols-6 gap-5 items-end" data-testid="create-code-form">
        <div className="space-y-2 md:col-span-1">
          <Label>Code (optional)</Label>
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="Auto-generate" className="h-11 font-mono-dt" data-testid="code-input" />
        </div>
        <div className="space-y-2">
          <Label>Free for (months)</Label>
          <Input type="number" min={1} max={120} value={f.months} onChange={(e) => setF({ ...f, months: e.target.value })} placeholder="Forever" className="h-11" data-testid="code-months-input" />
        </div>
        <div className="space-y-2">
          <Label>Plan</Label>
          <select value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} className="h-11 w-full rounded-lg border border-line bg-white px-3" data-testid="code-plan-select">
            <option value="">Any plan</option><option value="monthly">Basic only</option><option value="pro">Pro only</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label>Max uses</Label>
          <Input type="number" min={1} value={f.max_redemptions} onChange={(e) => setF({ ...f, max_redemptions: e.target.value })} placeholder="Unlimited" className="h-11" data-testid="code-max-input" />
        </div>
        <div className="space-y-2">
          <Label>Note</Label>
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="e.g. Grace Church" className="h-11" data-testid="code-note-input" />
        </div>
        <button disabled={busy} className="btn-navy !py-2.5 flex items-center justify-center gap-2" data-testid="create-code-btn"><Plus size={16} /> Generate code</button>
      </form>

      <div className="card-soft mt-6 overflow-hidden">
        {codes.length === 0 ? (
          <p className="p-10 text-center text-muted-ink" data-testid="codes-empty">No codes yet. Generate your first one above.</p>
        ) : (
          <table className="w-full text-left">
            <thead className="bg-paper text-xs uppercase tracking-[0.12em] text-muted-ink">
              <tr><th className="px-6 py-4">Code</th><th className="px-6 py-4">Free for</th><th className="px-6 py-4">Uses</th>
                <th className="px-6 py-4 hidden md:table-cell">Redeemed by</th><th className="px-6 py-4">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {codes.map((c) => (
                <tr key={c.code_id} data-testid={`code-row-${c.code}`}>
                  <td className="px-6 py-4">
                    <button onClick={() => copy(c.code)} className="font-mono-dt text-navy font-medium flex items-center gap-2 hover:text-teal" data-testid={`copy-code-${c.code}`}>{c.code} <Copy size={14} /></button>
                    {c.note && <p className="text-xs text-muted-ink">{c.note}</p>}
                  </td>
                  <td className="px-6 py-4 text-sm">{c.months ? `${c.months} month${c.months > 1 ? "s" : ""}` : "Forever"}{c.plan ? ` · ${c.plan === "pro" ? "Pro" : "Basic"} only` : ""}</td>
                  <td className="px-6 py-4 text-sm">{c.redemptions}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                  <td className="px-6 py-4 text-sm text-muted-ink hidden md:table-cell">{c.redeemed_by.map((r) => r.church_name).join(", ") || "–"}</td>
                  <td className="px-6 py-4">
                    <button onClick={() => toggle(c)} data-testid={`toggle-code-${c.code}`}
                      className={`text-xs rounded-full px-3 py-1 ${c.active ? "bg-teal-soft text-teal" : "bg-paper text-muted-ink"}`}>{c.active ? "Active" : "Disabled"}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <AdminReports />
      <StripeReconcile />
      <AdminChurches />
      <PcoAppSettings />
      <PcoConnect church={church} />
    </div>
  );
}
